//Library imports
import { Fragment, useState, useEffect } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useLocation } from 'react-router-dom';
import { monthsOfYear, TimesheetStatus } from '../../../common/constants';
import { selectOption } from '../../../types/selectOption';
import moment from "moment";

//Services
import { getTherapistNamesAndIds } from '../../../api/services/therapistService';
import { getDistrictOptions } from '../../../api/services/districtService';
import { getDisciplines } from '../../../api/services/disciplineService';
import { getDivisionOptions } from "../../../api/services/divisionService";
import { getTimesheets } from "../../../api/services/timesheetService";

//Requests
import TimesheetSearchRequest from "../../../api/requests/timesheetSearchRequest";

//Models

//Components
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import { Notification } from '../../../components/notifications';
import { FieldInput } from '../../../components/fieldInput/index';
import DataTable, { Button } from '../../../components/dataTable';
import { generateOptions } from '../../../common/utils';
import { getProgramsByDistrict } from '../../../api/services/programService';
import Select from 'react-select';
import { option } from '../../../api/models/option';
import DatePicker from 'react-datepicker';
import CustomAlert from '../../../components/alert/commonAlert';

const getFormattedTimeFromMinutes = (totalMinutes: number): string => {
    const duration = moment.duration(totalMinutes, 'minutes');
    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.asMinutes()) - hours * 60;
    return `${hours}h ${minutes}m`;
}

//Get our view state
type ViewState = {
    message?: string;
    ids?: [];
};

const now = new Date();

/**
 * Methods to fetch search criteria with which to populate
 */
const fetchTherapists = async () => await getTherapistNamesAndIds();
const fetchDistricts = async () => getDistrictOptions();
const fetchDivisions = async () => getDivisionOptions();
const fetchDisciplines = async () => getDisciplines();

const TimeSheetSearch = () => {
    //Process our DataTable columns and data
    const columns = () => [
        {
            Header: 'TimesheetId',
            accessor: (u: any) => {
                return `${u?.timesheetId}`;
            },
            className: 'hidden',
        },
        {
            Header: 'Therapist',
            accessor: (u: any) => {
                return `${u?.therapistName ?? ''}`;
            }
        },
        {
            Header: 'Discipline',
            accessor: (u: any) => {
                return `${u?.discipline ?? ''}`;
            }
        },
        {
            Header: 'Division',
            accessor: (u: any) => {
                return `${u?.divisionName ?? ''}`;
            }
        },
        {
            Header: 'District',
            accessor: (u: any) => {
                return `${u?.districtName ?? ''}`;
            }
        },
        {
            Header: 'Program',
            accessor: (u: any) => {
                return `${u?.programName ?? ''}`;
            }
        },
        {
            Header: 'Start Date',
            accessor: (u: any) => {
                return `${u?.startDate && moment(u?.startDate).isValid() ? moment(u?.startDate).format('MM/DD/YYYY') : ''}`;
            }
        },
        {
            Header: 'End Date',
            accessor: (u: any) => {
                return `${u?.endDate && moment(u?.endDate).isValid() ? moment(u?.endDate).format('MM/DD/YYYY') : ''}`;
            }
        },
        {
            Header: 'Status',
            accessor: (u: any) => {
                return `${u?.timesheetStatusName}`;
            }
        },
        {
            Header: 'Total',
            accessor: (u: any) => {
                return `${getFormattedTimeFromMinutes(u?.total)}`;
            }
        },
    ];

    //App hooks
    const history = useHistory();
    const { state } = useLocation<ViewState>();

    //Select element options
    const [therapistOptions, setTherapistOptions] = useState<selectOption[]>([{ value: -1, label: "" }]);
    const [districtOptions, setDistrictOptions] = useState<option[]>([{ value: -1, label: '' }]);
    const [disciplineOptions, setDisciplineOptions] = useState<option[]>([{ value: -1, label: '' }]);
    const [divisionOptions, setDivisionOptions] = useState<option[]>([{ value: -1, label: '' }]);
    const [programOptions, setProgramOptions] = useState<option[]>([{ value: -1, label: '' }]);
    const [timesheetStatusOptions, setTimesheetStatusOptions] = useState<option[]>([{ value: -1, label: '' }]);

    /**
     * Options lists (for our select elements)
     */
    const getTherapistsAutoCompleteAsync = useAsync({
        promiseFn: fetchTherapists,
        onResolve: (u) => setTherapistOptions([...u.data])
    });

    const districtsData = useAsync({
        promiseFn: fetchDistricts,
        onResolve: (u) => {
            let options: option[] = districtOptions;
            u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
            setDistrictOptions(options);
        },
    });

    const divisionAsync = useAsync({
        promiseFn: fetchDivisions,
        onResolve: (u) => {
            let options: option[] = divisionOptions;
            u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
            options.shift();
            setDivisionOptions(options);
        },
    });

    const disciplinesData = useAsync({
        promiseFn: fetchDisciplines,
        onResolve: (u) => {
            let options: option[] = disciplineOptions;

            //set options           
            (u.data.disciplines.map((g) => (
                options.push({ value: g.disciplineId ?? -1, label: g.name })
            )));

            setDisciplineOptions(options);
        },
    });

    const timesheetStatus = () => {
        let options: option[] = timesheetStatusOptions;

        //set options
        for (let status in TimesheetStatus) {
            if (isNaN(Number(status))) {

                var statusLabel = '';
                switch (status) {
                    case 'InProgress':
                        statusLabel = 'In Progress';
                        break;
                    case 'No_Timesheet':
                        statusLabel = 'No Timesheet';
                        break;
                    default:
                        statusLabel = status;
                        break;
                }

                options.push({ value: TimesheetStatus[status as keyof typeof TimesheetStatus], label: statusLabel })
            }
        }

        setTimesheetStatusOptions(options);
    };

    useEffect(() => {
        timesheetStatus();

        if (districtId > 0) {
            updatePrograms(districtId, divisionId)
        }
    }, []);

    const getYearOptions = () => {
        let yearOptions: option[] = [];
        Array.apply(0, Array(10)).map(function (x, i) {
            let target = now.getFullYear() - i;
            yearOptions.push({ label: target.toString(), value: target } as option);
        });

        return yearOptions;
    }

    //Data hooks
    const [searchData, setSearchData] = useState<[]>([]);
    const [therapistName, setName] = useState('');
    const [districtId, setDistrictId] = useState<number>(-1);
    const [month, setMonth] = useState<number>(now.getMonth() + 1);
    const [year, setYear] = useState<number>(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(month);
    const [selectedYear, setSelectedYear] = useState<number>(year);
    const [timesheetStatusId, setTimesheetStatus] = useState<number>(-1);
    const [tricoreId, setTricoreId] = useState<number | undefined>();
    const [disciplineId, setDisciplineId] = useState<number>(-1);
    const [divisionId, setDivisionId] = useState<number>(-1);
    const [programId, setProgramId] = useState<number>(-1);
    const [startDate, setStartDate] = useState<Date>(moment().startOf('isoWeek').toDate());
    const [endDate, setEndDate] = useState<Date | null>(moment().endOf('isoWeek').toDate());
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string>();
    
    const monthsOptions = monthsOfYear.map(s => ({ label: s.name, value: s.value } as option));
    const yearOptions = getYearOptions();

    useEffect(() => {
        let dateString = localStorage.getItem("timesheetSearchTime");
        let date = moment(dateString);

        if (date && moment().isAfter(date.add(10, 'minutes'))) {
            localStorage.removeItem("timesheetSearchData");
            localStorage.removeItem("timesheetSearchTime");
            localStorage.removeItem("timesheetSearchFilter")
            return;
        }

        let dataString = localStorage.getItem("timesheetSearchData");

        if (dataString) {
            let filterString = localStorage.getItem("timesheetSearchFilter");
            if (filterString) {
                let filterData = JSON.parse(filterString);
                setName(filterData.therapistName);
                setDistrictId(filterData.districtId)
                setSelectedMonth(filterData.selectedMonth)
                setSelectedYear(filterData.selectedYear)
                setTimesheetStatus(filterData.timesheetStatusId)
                setTricoreId(filterData.tricoreId)
                setDisciplineId(filterData.disciplineId)
                setDivisionId(filterData.divisionId)
                setProgramId(filterData.programId)
            }

            let data = JSON.parse(dataString);
            setSearchData(data);
        }
        

    }, []); const handleTimesheetSearchResponse = (response: any) => {
        setSearchData(response.data);
        setFilterData();
        let dataString = JSON.stringify(response.data);
        let currentDateTime = moment().toString();
        localStorage.setItem("timesheetSearchTime", currentDateTime)
        localStorage.setItem("timesheetSearchData", dataString);
    };

    const setFilterData = () => {
        let filterData = {
            therapistName: therapistName,
            districtId: districtId,
            //selectedMonth: selectedMonth,
            //selectedYear: selectedYear,
            startDate: setStartDate,
            endDate: setEndDate,
            timesheetStatusId: timesheetStatusId,
            tricoreId: tricoreId,
            disciplineId: disciplineId,
            divisionId: divisionId,
            programId: programId
        }

        let filterDataString = JSON.stringify(filterData);
        localStorage.setItem("timesheetSearchFilter", filterDataString);
    }

    const timesheetSearchDefer = async (args: any[]) => {
        const request: TimesheetSearchRequest = {
            startDate: startDate?.toLocaleDateString(),
            endDate: endDate!?.toLocaleDateString(),
            therapistName: therapistName,
            disciplineId: disciplineId,
            districtId: districtId,
            divisionId: divisionId,
            timesheetStatusId: timesheetStatusId,
            programId: programId,
            tricoreId: (!tricoreId ? undefined : tricoreId),
        }
        return await getTimesheets(request);
    }

    const searchTimesheetsAsync = useAsync({
        deferFn: timesheetSearchDefer,
        onResolve: handleTimesheetSearchResponse,
    });

    const updatePrograms = async (district: number, division: number) => {
        const response = await getProgramsByDistrict(district, division);

        if (response.data.programs.length === 0) {
            setProgramOptions([{ value: -1, label: '' }]);
            return true;
        }

        const programs = generateOptions(response.data.programs, 'programId', 'name', true);
        setProgramOptions(programs);
    }

    useEffect(() => {
        updatePrograms(districtId, divisionId)
    }, [districtId]);

    useEffect(() => {
        updatePrograms(districtId, divisionId)
    }, [divisionId]);

    function resetFilter() {
        setName('');
        setDistrictId(-1)
        // setSelectedMonth(now.getMonth() + 1)
        // setSelectedYear(now.getFullYear())
        setStartDate(moment().startOf('isoWeek').toDate());
        setEndDate(moment().endOf('isoWeek').toDate());
        setTimesheetStatus(-1)
        setTricoreId(undefined)
        setDisciplineId(-1)
        setDivisionId(-1)
        setProgramId(-1)

        setFilterData();
    }
    const handleCloseAlert = () => {
        setShowAlert(false);
      };

    return (
        <Layout isLoading={
            searchTimesheetsAsync.isPending ||
            getTherapistsAutoCompleteAsync.isPending ||
            disciplinesData.isPending ||
            divisionAsync.isPending ||
            districtsData.isPending
        }>
            <Fragment>
                {state?.message && <Notification>{state?.message}</Notification>}

                {/* Primary Container */}
                <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                    {/* Search Fields */}
                    <div className='z-0 grid grid-cols-6 gap-6 mb-5'>
                        <div className='col-span-3 sm:col-span-3 lg:col-span-1'>
                            <label className='primary'>Start Date</label>
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => {
                                    if (date) {
                                        const maxEndDate = new Date(date as Date);
                                        maxEndDate.setDate(maxEndDate.getDate() + 90); // Set max end date to 90 days after new start date

                                        // Check if current end date is within the new valid range
                                        if (endDate && endDate > maxEndDate) {
                                            setAlertMessage("The selected end date exceeds the 90-day limit from the new start date. Please choose a valid end date.");
                                            setShowAlert(true)
                                            setEndDate(null);
                                        }
                                    }
                                    setStartDate(date as Date)
                                }}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={endDate}
                                className="border p-2 rounded"
                                placeholderText="Select a start date"
                            />

                        </div>
                        <div className='col-span-3 sm:col-span-3 lg:col-span-1'>
                            <label className='primary'>End Date</label>
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => {
                                    if (startDate && date) {
                                        const maxEndDate = new Date(startDate);
                                        maxEndDate.setDate(maxEndDate.getDate() + 90); // Add 90 days to start date

                                        if (date > maxEndDate) {
                                            setAlertMessage("End date cannot exceed 90 days from the start date.");
                                            setShowAlert(true);
                                            return;
                                        }
                                    }
                                    setEndDate(date as Date);
                                }}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate} // Prevents selecting an end date before the start date
                                className="border p-2 rounded"
                                placeholderText="Select a End date"
                                disabled={!startDate}
                            />
                        </div>
                    </div>
                    {showAlert && (
                        <CustomAlert
                            message={alertMessage!}
                            onClose={handleCloseAlert}
                        />
                    )}
                    <div className='z-0 grid grid-cols-6 gap-6'>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FieldInput
                                type='text'
                                value={therapistName}
                                rounded='sm'
                                placeholder=''
                                label={`Therapist`}
                                onChange={(e) => { setName(e.target.value); }}
                                icon={'search'}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Discipline</label>
                            <Select
                                className='block mt-1 primary'
                                value={{ label: disciplineOptions.find(x => x.value === disciplineId)?.label, value: disciplineId } as option}
                                name='test'
                                options={disciplineOptions ?? []}
                                placeholder={`Select Discipline`}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: -1 };
                                    setDisciplineId(Number(e.value));
                                }}
                                isSearchable={false}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Status</label>
                            <Select
                                className='block mt-1 primary'
                                value={{ label: timesheetStatusOptions.find(x => x.value === timesheetStatusId)?.label, value: timesheetStatusId } as option}
                                name='test'
                                options={timesheetStatusOptions ?? []}
                                placeholder={`Select Status`}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: -1 };
                                    setTimesheetStatus(Number(e.value));
                                }}
                                isSearchable={false}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FieldInput
                                label='Tricore ID/CMS ID'
                                value={tricoreId?.toString()}
                                onChange={(e: any) => setTricoreId(e.currentTarget.value)}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>District</label>
                            <Select
                                className='block mt-1 primary'
                                value={{ label: districtOptions.find(x => x.value === districtId)?.label, value: districtId } as option}
                                name='test'
                                options={districtOptions ?? []}
                                placeholder={`Select District`}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: -1 };
                                    setDistrictId(Number(e.value));
                                }}
                                isSearchable={false}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Division</label>
                            <Select
                                className='block mt-1 primary'
                                value={{ label: divisionOptions.find(x => x.value === divisionId)?.label, value: divisionId } as option}
                                name='test'
                                options={divisionOptions ?? []}
                                placeholder={`Select Division`}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: -1 };
                                    setDivisionId(Number(e.value));
                                }}
                                isSearchable={false}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Program</label>
                            <Select
                                className='block mt-1 primary'
                                value={{ label: programOptions.find(x => x.value === programId)?.label, value: programId } as option}
                                name='test'
                                options={programOptions ?? []}
                                placeholder={`Select Ptogram`}
                                onChange={(e: any | null) => {
                                    //Null exception handling
                                    e = (e !== null) ? e : { label: '', value: -1 };
                                    setProgramId(Number(e.value));
                                }}
                                isSearchable={false}
                            />
                        </div>
                    </div>

                    {/* Button Row */}
                    <div className="mt-6" style={{ display: 'flex' }}>
                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <SubmitButton
                                text='Search'
                                className='mr-5 primary button'
                                onClick={() => { searchTimesheetsAsync.run(); }}
                            />
                        </div>

                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <button
                                className='mr-5 primary button'
                                onClick={() => { resetFilter(); }}
                                style={{ backgroundColor: '#005782' }}
                            >
                                Reset filter
                            </button>
                        </div>
                    </div>
                </div>

                {/* Results Table */}
                {searchData !== null && searchData !== undefined && (
                    <div className='results-table'>
                        {searchData.length > 0 && (
                            <div className='overflow-hidden border-b border-gray-200 rounded-lg shadow'>
                                <DataTable
                                    columns={columns()}
                                    data={searchData}
                                    showPagination={true}
                                    skipFilter={true}
                                    getRowProps={(row: any) => ({
                                        onClick: () => history.push(`/admin/timesheetSearch/${row.values.TimesheetId}`),
                                    })}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* No timesheets found message */}
                {searchData.length === 0 && (
                    <div>
                        <label>No timesheets found.</label>
                        <div className='px-4 py-3 bg-gray-100 sm:px-6 sm:flex sm:flex-row-reverse'>

                        </div>
                    </div>
                )}
            </Fragment>
        </Layout>
    )
}

export default TimeSheetSearch;